import * as React from "react";
import { cn } from "../lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
}

// 체크박스 팝오버 멀티셀렉트. 선택 없음 = 전체(allLabel).
// Select 와 동일한 외형(h-[34px])을 맞춰 필터바에 섞여도 어색하지 않게.
export function MultiSelect({
  options,
  selected,
  onChange,
  allLabel = "전체",
  className,
  menuWidth = 260,
}: {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel?: string;
  className?: string;
  menuWidth?: number;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const selectedSet = new Set(selected);
  const summary =
    selected.length === 0 ? allLabel : `${allLabel} · ${selected.length}개 선택`;

  function toggle(value: string) {
    const next = selectedSet.has(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  }

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "h-[34px] pl-2.5 pr-7 rounded-base border border-border-strong bg-surface text-xs text-ink-muted cursor-pointer outline-none focus:border-primary inline-flex items-center relative",
          open && "border-primary",
          className,
        )}
      >
        <span className="truncate">{summary}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="absolute right-2 pointer-events-none"
          aria-hidden
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-[38px] left-0 z-20 rounded-base border border-border-strong bg-surface shadow-lg py-1 max-h-[320px] overflow-y-auto"
          style={{ width: menuWidth }}
        >
          <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-border">
            <span className="text-[11px] text-ink-subtle">
              {selected.length > 0 ? `${selected.length}개 선택됨` : "전체"}
            </span>
            {selected.length > 0 && (
              <button
                type="button"
                className="text-[11px] text-primary hover:underline"
                onClick={() => onChange([])}
              >
                전체 해제
              </button>
            )}
          </div>
          {options.length === 0 ? (
            <div className="px-2.5 py-3 text-[12px] text-ink-subtle">
              항목이 없습니다.
            </div>
          ) : (
            options.map((o) => {
              const on = selectedSet.has(o.value);
              return (
                <button
                  type="button"
                  key={o.value}
                  onClick={() => toggle(o.value)}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[12px] text-ink hover:bg-canvas"
                >
                  <span
                    className={cn(
                      "w-3.5 h-3.5 rounded-[3px] border flex items-center justify-center shrink-0",
                      on
                        ? "bg-primary border-primary text-white"
                        : "border-border-strong bg-surface",
                    )}
                  >
                    {on && (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        aria-hidden
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
